import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import Button from "components/shared/Button";
import useGraphQLQuery from "hooks/api/GQL";
import { TAssetParamsWithSubmissionStatus } from "hooks/api/GQL/assets/useAssets.models";
import queryConfig from "hooks/api/GQL/queryConfig";
import useIsFeatureFlagOn from "hooks/useIsFeatureFlagOn";
import {
  IAssetsResponseDataGraphQL,
  IGenericAsset,
} from "models/assets.models";
import { INotification } from "models/notificationItem.models";
import { QueryAPIKey } from "utils/constants/api.constants";
import { FeatureFlagName } from "utils/constants/featureFlags.constants";
import {
  getCTALink,
  getReadableCTAType,
  shouldResolveAssetForCTA,
} from "./CallToAction.utils";

type TProps = {
  data: INotification;
};

const CallToAction = ({ data }: TProps) => {
  const {
    metadata: { ctaType, assetId, assetSourceArn },
  } = data;

  const { t } = useTranslation("common");

  const isSimplifyContentEnabled = useIsFeatureFlagOn(
    FeatureFlagName.EnableSimplifyContent,
  );

  const assetLookupId = assetSourceArn ?? assetId ?? "";
  const shouldResolveAsset = shouldResolveAssetForCTA(data) && !!assetLookupId;

  const { data: assetsData, isLoading } = useGraphQLQuery<
    IAssetsResponseDataGraphQL<IGenericAsset>,
    TAssetParamsWithSubmissionStatus,
    IGenericAsset[]
  >({
    ...queryConfig.getAssets,
    queryKey: [QueryAPIKey.GetAsset, assetLookupId],
    variables: {
      includeAssetSubmissionStatus: isSimplifyContentEnabled,
      ...(shouldResolveAsset
        ? assetSourceArn
          ? { sourceArns: [assetSourceArn] }
          : assetId
            ? { assetIds: [assetId] }
            : {}
        : {}),
    },
    queryOptions: {
      ...queryConfig.getAssets.queryOptions,
      enabled: shouldResolveAsset,
    },
  });

  const subjectLink = getCTALink(data, assetsData?.[0]);

  if (!ctaType || (!subjectLink && !isLoading)) {
    return null;
  }

  return (
    <Button
      loading={isLoading}
      {...(!isLoading ? { component: Link, to: subjectLink } : {})}
      variant="outlined"
      color="primary"
      size="small"
      data-qaid="notification-item-button-link"
    >
      {t("navigation.notifications.templateButtonText.view", {
        subject: ctaType ? getReadableCTAType(data, t) : "",
      })}
    </Button>
  );
};

export default CallToAction;
